import React from 'react';
import {
  ExternalDropZone,
  Upload,
  UploadOnAddEvent
} from '@progress/kendo-react-upload';
import cloneDeep from 'lodash.clonedeep';
import { useParams } from 'react-router-dom';
import { Error } from '@progress/kendo-react-labels';

import { MediaCard } from './task-media/media-grid';
import { useReels } from '../../../../contexts/reels';
import { uuid } from '../../../../types/common-helper';
import { useUploadReelsResources } from '../../../../hooks/use-reels';

const allowedFile = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const UploadReelsResource: React.FC<{
  allowMultipleUpload?: boolean;
  selectWhenDone?: boolean;
  children?: (args: {
    latestUploaded: {
      id: string;
      url: string;
      thumbnail: string;
    }[];
    isUploading: boolean;
  }) => React.ReactNode;
}> = ({ allowMultipleUpload = true, selectWhenDone = false, children }) => {
  const { gameCode } = useParams();

  const uploadRef = React.useRef<Upload>(null);
  const [errorMessage, setErrorMessage] = React.useState<{
    extension: string[];
    size: string[];
  }>({
    extension: [],
    size: []
  });
  const [latestUploaded, setLatestUploaded] = React.useState<
    {
      id: string;
      url: string;
      thumbnail: string;
    }[]
  >([]);

  const { resources, global_settings, handleChange } = useReels();
  const { isMutating, trigger } = useUploadReelsResources(gameCode ?? '');

  const onUploadClick = () => {
    if (uploadRef && uploadRef.current && uploadRef.current.actionElement) {
      uploadRef.current.actionElement.click();
    }
  };

  const onDropFiles = (event: UploadOnAddEvent) => {
    setErrorMessage({
      extension: [],
      size: []
    });
    const files: File[] = [];
    const errorExtension: string[] = [];
    const errorSize: string[] = [];

    event.newState.forEach((file, idx) => {
      if (!allowMultipleUpload && idx !== 0) return;

      if (file) {
        if (file.validationErrors) {
          if (file.validationErrors.includes('invalidFileExtension'))
            errorExtension.push(file.name);
          if (file.validationErrors.includes('invalidMaxFileSize'))
            errorSize.push(`${file.name} (${formatBytes(file.size ?? 0, 0)})`);

          return;
        }

        if (file.getRawFile) files.push(file.getRawFile());
      }
    });

    setErrorMessage({
      extension: errorExtension,
      size: errorSize
    });

    if (files.length > 0)
      trigger(
        { gameCode: gameCode ?? '', files },
        {
          onSuccess: (result) => {
            const uploaded = result
              .filter((e) => e !== undefined)
              .map((e) => ({
                id: uuid(),
                url: e.url ?? '',
                thumbnail: e.url ?? ''
              }));
            setLatestUploaded(uploaded);

            const changes: any = { resources: [...resources, ...uploaded] };
            if (selectWhenDone && global_settings.watermark) {
              const newGlobalSettings = cloneDeep(global_settings);
              newGlobalSettings.watermark!.id = uploaded[0].id;
              newGlobalSettings.watermark!.url = uploaded[0].url;
              changes.global_settings = newGlobalSettings;
            }

            handleChange(changes);
          }
        }
      );
  };

  return (
    <div className={'reels-resource-container'}>
      <Upload
        className={'d-none'}
        ref={uploadRef}
        batch={false}
        multiple={allowMultipleUpload}
        withCredentials={false}
        restrictions={{
          allowedExtensions: allowedFile,
          maxFileSize: 512000
        }}
        accept={allowedFile.join(',')}
        onAdd={onDropFiles}
        showFileList
      />

      <div
        className={'d-flex flex-column'}
        style={{
          marginBottom: '24px'
        }}>
        <ExternalDropZone
          uploadRef={uploadRef}
          disabled={isMutating}
          className={'mb-2'}
          customNote={
            'Only .jpg, .jpeg, .png, .gif, .webp files are allowed. Max 500KB.'
          }
          customHint={
            <div className={'d-flex flex-column gap-2'}>
              <span
                className="material-symbols-outlined"
                style={{
                  color: '#a5b7bf',
                  fontSize: '32px'
                }}>
                upload
              </span>
              <span>
                Drag and drop {allowMultipleUpload ? 'images' : 'an image'} here
              </span>
              <span
                style={{
                  fontSize: '14px'
                }}>
                or{' '}
                <span
                  className={'text-primary cursor-pointer'}
                  onClick={onUploadClick}>
                  browse
                </span>
              </span>
            </div>
          }
        />
        <div className={'d-flex flex-column gap-1'}>
          {errorMessage.extension.length > 0 && (
            <Error className={'m-0'}>
              {`Invalid file type for: ${errorMessage.extension.join(', ')}.`}
            </Error>
          )}
          {errorMessage.size.length > 0 && (
            <Error className={'m-0'}>
              {`File exceeds the 500 KB limit: ${errorMessage.size.join(', ')}. Please upload a smaller file.`}
            </Error>
          )}
        </div>
      </div>

      {children?.({ latestUploaded, isUploading: isMutating })}
    </div>
  );
};

export const Resources: React.FC = () => {
  const { timeline, resources, handleChange } = useReels();

  return (
    <div className={'d-flex flex-column flex-grow-1 w-100 overflow-auto'}>
      <UploadReelsResource />

      <div className={'reels-tasks-media-grid-container'}>
        {resources.map((res) => (
          <MediaCard item={{ id: res.id, url: res.url, type: 'image' }}>
            <div
              className={
                'd-flex align-items-center justify-content-center bg-white p-1 position-absolute'
              }
              style={{
                bottom: '8px',
                right: '8px',
                borderRadius: '4px',
                zIndex: 999
              }}
              title={'Remove from resources'}
              onClick={() => {
                const resourceId = resources.findIndex((r) => r.id === res.id);
                if (resourceId !== -1) {
                  const finalResources = cloneDeep(resources);
                  finalResources.splice(resourceId, 1);

                  const timelineId = timeline.findIndex((t) => t.id === res.id);
                  let finalTimeline: typeof timeline | undefined = undefined;
                  if (timelineId !== -1) {
                    finalTimeline = cloneDeep(timeline);
                    finalTimeline.splice(timelineId, 1);
                  }

                  handleChange({
                    resources: finalResources,
                    ...(finalTimeline !== undefined && {
                      timeline: finalTimeline
                    })
                  });
                }
              }}>
              <span
                className="material-symbols-outlined"
                style={{
                  color: 'red',
                  fontSize: '16px'
                }}>
                delete
              </span>
            </div>
          </MediaCard>
        ))}
      </div>
    </div>
  );
};
